/**
 * Who did something to a document, as the portal prints it (#110).
 *
 * A linked phone acts as an `INSTALL`, and the server names it
 * `<label> · App DAC (<platform> · <id8>)`. That shape is documented on `ActorRef.display` in the
 * contract and pinned by `backend/test/link-codes.test.ts`. The label is the office's name for the
 * phone **at the time of the act** (renaming the phone does not rewrite history), and it can
 * contain ` · ` itself ("Juan Pérez · 1234 ABC"), so the suffix is removed from the end rather than
 * the string split on its first separator.
 *
 * Parsing rather than joining: a document's actor reaches the portal with no id (`decas.ts` strips
 * it), and the label in it is a snapshot the phone list would not reproduce after a rename anyway.
 */

import type { TFunction } from 'i18next'
import type { ActorRef } from '@deca/api'

const APP_SUFFIX = / · App DAC \([^()]*\)$/

/** The phone's label when this actor is a linked phone; null for anyone else. */
export function appLinkLabel(actor: Pick<ActorRef, 'type' | 'display'> | null | undefined): string | null {
  if (actor?.type !== 'INSTALL') return null
  const label = actor.display.replace(APP_SUFFIX, '')
  // Nothing left (a display that is the suffix alone): the whole display still says which phone.
  return label.length > 0 ? label : actor.display
}

/** "Desde la app · <label>" for a phone; the server's own display for anyone else. */
export function actorText(actor: Pick<ActorRef, 'type' | 'display'>, t: TFunction): string {
  const label = appLinkLabel(actor)
  return label === null ? actor.display : t('marks.fromApp', { label })
}
